import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export type Cursor = Record<string, any> | null;

export interface CursorPage<T> {
  items: T[];
  nextCursor: Cursor;
  hasMore: boolean;
  total?: number;
}

interface UseInfiniteDataOptions<T> {
  /** How many items to fetch per chunk */
  limit?: number;
  /**
   * Cache key to enable instant navigation (Notion-like).
   * Example: "leads", "payments", "projects:archived"
   */
  cacheKey?: string;
  /** Cache time-to-live in ms (default: 2 minutes) */
  cacheTtlMs?: number;
  service: {
    getCursorPage: (limit: number, cursor: Cursor) => Promise<CursorPage<T>>;
  };
}

interface UseInfiniteDataReturn<T> {
  items: T[];
  total?: number;
  loadingInitial: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Première page avec nouveaux filtres, sans vider la liste (évite le scroll en haut). */
  replaceWithFirstPage: () => Promise<void>;
  reset: () => void;
  // Low-level optimistic ops (used by useOptimisticCRUD)
  addItem: (item: T) => void;
  updateItem: (item: T) => void;
  removeItem: (id: string) => void;
  replaceItem: (tempId: string, realItem: T) => void;
}

const dedupeById = <T,>(existing: T[], incoming: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of existing) {
    const id = (item as any)?.id as string | undefined;
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(item);
    }
  }
  for (const item of incoming) {
    const id = (item as any)?.id as string | undefined;
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(item);
    }
  }
  return out;
};

type InfiniteCacheEntry<T> = {
  items: T[];
  total?: number;
  hasMore: boolean;
  cursor: Cursor;
  updatedAt: number;
};

// Module-level cache (survives route changes; cleared by TTL/user change)
const infiniteCache = new Map<string, InfiniteCacheEntry<any>>();

/**
 * useInfiniteData
 * Notion-like list loading: cursor pagination + load more + SWR refresh.
 */
export const useInfiniteData = <T extends { id: string }>(
  options: UseInfiniteDataOptions<T>
): UseInfiniteDataReturn<T> => {
  const { limit = 30, service, cacheKey, cacheTtlMs = 2 * 60 * 1000 } = options;
  const { user, loading: authLoading } = useAuth();

  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const cursorRef = useRef<Cursor>(null);
  const inFlightRef = useRef<AbortController | null>(null);
  const latestRequestIdRef = useRef(0);
  const hydratedFromCacheRef = useRef(false);

  const fullCacheKey = useMemo(() => {
    if (!cacheKey || !user?.id) return null;
    // Include limit in the key to avoid mixing different chunk sizes.
    return `${user.id}:${cacheKey}:${limit}`;
  }, [cacheKey, user?.id, limit]);

  const reset = useCallback(() => {
    inFlightRef.current?.abort();
    inFlightRef.current = null;
    cursorRef.current = null;
    // Preserve optimistic items (temp IDs) when resetting
    setItems((prev) => prev.filter(item => (item as any).id?.startsWith('temp-')));
    setTotal(undefined);
    setError(null);
    setHasMore(true);
    setLoadingInitial(false);
    setLoadingMore(false);
    setRefreshing(false);
    hydratedFromCacheRef.current = false;
    if (fullCacheKey) {
      infiniteCache.delete(fullCacheKey);
    }
  }, [fullCacheKey]);

  const fetchPage = useCallback(
    async (mode: 'initial' | 'more' | 'refresh' | 'replace') => {
      if (authLoading) {
        setLoadingInitial(true);
        return;
      }
      if (!user?.id) {
        reset();
        return;
      }

      if (mode === 'initial') setLoadingInitial(true);
      if (mode === 'more') setLoadingMore(true);
      if (mode === 'refresh' || mode === 'replace') setRefreshing(true);

      setError(null);

      // Cancel previous in-flight request (Notion-like: only latest matters)
      inFlightRef.current?.abort();
      const controller = new AbortController();
      inFlightRef.current = controller;

      const requestId = ++latestRequestIdRef.current;

      try {
        const cursor = mode === 'more' ? cursorRef.current : null;
        const page = await service.getCursorPage(limit, cursor);

        if (controller.signal.aborted) return;
        if (latestRequestIdRef.current !== requestId) return;

        cursorRef.current = page.nextCursor;
        setHasMore(page.hasMore);
        if (typeof page.total === 'number') setTotal(page.total);

        if (mode === 'more') {
          setItems((prev) => dedupeById(prev, page.items));
        } else if (mode === 'replace') {
          setItems((prev) => {
            const optimisticItems = prev.filter(item => (item as any).id?.startsWith('temp-'));
            return [...optimisticItems, ...page.items];
          });
        } else {
          // initial/refresh: replace with latest chunk (SWR)
          // Preserve optimistic items (temp IDs) when refreshing to maintain instant UI feedback
          setItems((prev) => {
            const optimisticItems = prev.filter(item => (item as any).id?.startsWith('temp-'));
            if (prev.length === 0) {
              return [...optimisticItems, ...page.items];
            }
            // Merge server items with existing items, preserving optimistic ones
            const merged = dedupeById(page.items, prev);
            // Ensure optimistic items are still present (they haven't been created server-side yet)
            const mergedIds = new Set(merged.map(i => i.id));
            optimisticItems.forEach(opt => {
              if (!mergedIds.has(opt.id)) {
                merged.unshift(opt);
              }
            });
            return merged;
          });
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
      } finally {
        if (latestRequestIdRef.current === requestId) {
          if (mode === 'initial') setLoadingInitial(false);
          if (mode === 'more') setLoadingMore(false);
          if (mode === 'refresh' || mode === 'replace') setRefreshing(false);
        }
      }
    },
    [authLoading, user?.id, reset, service, limit]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || loadingInitial || refreshing) return;
    if (!hasMore) return;
    await fetchPage('more');
  }, [fetchPage, hasMore, loadingMore, loadingInitial, refreshing]);

  const refresh = useCallback(async () => {
    await fetchPage(items.length === 0 ? 'initial' : 'refresh');
  }, [fetchPage, items.length]);

  const replaceWithFirstPage = useCallback(async () => {
    await fetchPage('replace');
  }, [fetchPage]);

  // Low-level optimistic operations
  const addItem = useCallback((item: T) => {
    setItems((prev) => {
      // Check if item already exists (avoid duplicates)
      if (prev.some(i => i.id === item.id)) {
        return prev;
      }
      return [item, ...prev];
    });
  }, []);

  const updateItem = useCallback((item: T) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const replaceItem = useCallback((tempId: string, realItem: T) => {
    setItems((prev) => prev.map((i) => (i.id === tempId ? realItem : i)));
  }, []);

  // Initial load on auth change
  useEffect(() => {
    // Hydrate from cache for instant navigation (Notion-like)
    if (fullCacheKey) {
      const cached = infiniteCache.get(fullCacheKey) as InfiniteCacheEntry<T> | undefined;
      if (cached && Date.now() - cached.updatedAt <= cacheTtlMs) {
        hydratedFromCacheRef.current = true;
        cursorRef.current = cached.cursor;
        setItems(cached.items);
        setTotal(cached.total);
        setHasMore(cached.hasMore);
        setLoadingInitial(false);
        // Refresh in background (SWR) to keep data accurate
        void fetchPage('refresh');
        return;
      }
    }

    void fetchPage('initial');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Keep cache updated while mounted
  useEffect(() => {
    if (!fullCacheKey) return;
    if (!user?.id) return;
    // Don't cache empty initial state before first fetch completes
    if (loadingInitial && !hydratedFromCacheRef.current) return;

    infiniteCache.set(fullCacheKey, {
      items,
      total,
      hasMore,
      cursor: cursorRef.current,
      updatedAt: Date.now(),
    });
  }, [fullCacheKey, user?.id, items, total, hasMore, loadingInitial]);

  return useMemo(
    () => ({
      items,
      total,
      loadingInitial,
      loadingMore,
      refreshing,
      error,
      hasMore,
      loadMore,
      refresh,
      replaceWithFirstPage,
      reset,
      addItem,
      updateItem,
      removeItem,
      replaceItem,
    }),
    [
      items,
      total,
      loadingInitial,
      loadingMore,
      refreshing,
      error,
      hasMore,
      loadMore,
      refresh,
      replaceWithFirstPage,
      reset,
      addItem,
      updateItem,
      removeItem,
      replaceItem,
    ]
  );
};

